import { Animated } from 'react-native';
import { useEffect, useRef } from 'react';

/**
 * Creates an animated style based on a trigger and a from/to style property.
 *
 * @param trigger - The trigger to initiate the animation.
 * @param fromStyleProp - The style property value to transition from.
 * @param toStyleProp - The style property value to transition to.
 * @param duration - The duration of the animation.
 *
 * @returns The interpolated animated style property value.
 *
 * @example
 * ```typescript
 * const isHovered = useHover(buttonRef);
 * const animatedBackgroundColor = useInterpolatedAnimatedStyle(
 *   isHovered,
 *   BUTTON_BACKGROUND_COLORS[variant].default,
 *   BUTTON_BACKGROUND_COLORS[variant].hover,
 * );
 * // ...
 * return (
 *   <Animated.View
 *     style={[
 *       styles.button,
 *       { backgroundColor: animatedBackgroundColor },
 *       style,
 *     ]}
 *   >
 *     ...
 *   </Animated.View>
 * );
 * ```
 */
export const useInterpolatedAnimatedStyle = <T extends string | number>(
  trigger: boolean,
  fromStyleProp: T,
  toStyleProp: T,
  duration = 150,
) => {
  const animatedValueRef = useRef(new Animated.Value(0)).current;

  useEffect(() => {
    Animated.timing(animatedValueRef, {
      duration,
      toValue: trigger ? 1 : 0,
      useNativeDriver: false,
    }).start();
  }, [trigger, animatedValueRef, duration]);

  return animatedValueRef.interpolate({
    inputRange: [0, 1],
    // interpolate unable to infer output range from narrowed type
    outputRange: [fromStyleProp, toStyleProp] as T extends number
      ? number[]
      : string[],
  });
};
